/**
 * useShopInfo — derive the merchant slug from the URL and fetch the
 * shop's public branding info (name + logo + primary color).
 *
 * Used by every auth page (login, register, forgot, reset, verify-email)
 * and the AppLayout sidebar to render branded UI even before the
 * vendor logs in. Returns:
 *   - shopInfo: the resolved info (or null while loading)
 *   - isLoading: true during the initial fetch
 *   - notFound: true if the slug doesn't resolve to a live shop
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { shopApi, type PublicShopInfo } from '../api/shop';
import { useAuth } from '../contexts/AuthContext';
import {
  brandColorRgb,
  darkenBrandColor,
  normalizeBrandColor,
  readableBrandForeground,
} from '../utils/brandColor';
import { normalizeShopSlug } from '../utils/shopSlug';

const FALLBACK: PublicShopInfo = {
  found: false,
  shopify_domain: '',
  shop_name: null,
  portal_name: 'Vendor Portal',
  logo_url: '',
  primary_color: '#6366f1',
  login_url: '',
  register_url: '',
};

export function useShopInfo() {
  // Wave-61: derive slug in priority order:
  //   1. :shop_slug param when on slugged auth routes (/{slug}/login etc.)
  //   2. AuthContext user.shop_slug (set after login — works on
  //      /dashboard, /products, /orders, etc.)
  //   3. URL first segment IF it's not a known SPA route (defensive
  //      fallback for URLs like /vendors/{slug}/something-else)
  // Without #2, the sidebar on /vendors/dashboard fell through to
  // FALLBACK ("Vendor Portal" + "V" avatar) because `dashboard`
  // isn't a real shop slug.
  const params = useParams<{ shop_slug?: string }>();
  const { user } = useAuth();
  const slug = useMemo(() => {
    const routeSlug = normalizeShopSlug(params.shop_slug);
    if (routeSlug) return routeSlug;
    const userSlug = normalizeShopSlug(user?.shop_slug);
    if (userSlug) return userSlug;
    const KNOWN_ROUTES = new Set([
      'login', 'register', 'forgot-password', 'reset-password', 'verify-email',
      'dashboard', 'products', 'orders', 'inventory', 'payouts', 'returns',
      'coupons', 'reviews', 'questions', 'analytics', 'profile', 'support',
      'notifications', 'storefront', 'ai',
    ]);
    const segments = window.location.pathname.split('/').filter(Boolean);
    const after = segments[0] === 'vendors' ? segments.slice(1) : segments;
    const candidate = after[0];
    if (candidate && !KNOWN_ROUTES.has(candidate)) {
      return normalizeShopSlug(candidate);
    }
    return '';
  }, [params.shop_slug, user?.shop_slug]);

  const [shopInfo, setShopInfo] = useState<PublicShopInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      setShopInfo(FALLBACK);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    shopApi
      .getInfo(slug)
      .then((info) => {
        if (cancelled) return;
        setShopInfo(info);
      })
      .catch(() => {
        if (cancelled) return;
        setShopInfo({ ...FALLBACK, shopify_domain: slug });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const resolved = shopInfo ?? FALLBACK;

  // Apply merchant branding to the exact tokens consumed by Tailwind and
  // charts. Derive hover, soft-surface, and readable-foreground values so a
  // light merchant color cannot produce illegible primary controls.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const primary = normalizeBrandColor(resolved.primary_color);
    const { r, g, b } = brandColorRgb(primary);
    const primaryHover = darkenBrandColor(primary, 0.14);
    const hoverRgb = brandColorRgb(primaryHover);
    const root = document.documentElement;
    root.style.setProperty('--vendoriq-primary', primary);
    root.style.setProperty('--vendoriq-primary-rgb', `${r} ${g} ${b}`);
    root.style.setProperty('--vendoriq-primary-hover', primaryHover);
    root.style.setProperty(
      '--vendoriq-primary-hover-rgb',
      `${hoverRgb.r} ${hoverRgb.g} ${hoverRgb.b}`,
    );
    root.style.setProperty('--vendoriq-primary-soft', `rgba(${r}, ${g}, ${b}, 0.12)`);
    root.style.setProperty('--vendoriq-on-primary', readableBrandForeground(primary));
  }, [resolved.primary_color]);

  return {
    shopInfo: resolved,
    isLoading,
    notFound: !!shopInfo && !shopInfo.found && !!slug,
  };
}
